/**
 * Corpo do perfil do estabelecimento.
 *
 * Ordem: o que a gente faz (servicos), quem vai te atender (profissionais),
 * quando (horarios), o que levar para casa (produtos), planos de assinatura,
 * localizacao. A linha compacta de conta vem antes dos servicos. Produtos e planos so aparecem quando
 * existem — secao vazia diria que a loja nao tem nada, o que e diferente de
 * nao ter loja. Todos os caminhos
 * clicaveis levam ao mesmo lugar — o wizard na primeira etapa, sem carregar a
 * escolha (DEC-2 da spec).
 *
 * O botao de agendar fica fixo no rodape da tela, sempre ao alcance, em vez de
 * esperar no fim do conteudo. Aqui nao existe "Proximo": ninguem avanca sem
 * escolher.
 */

import Link from "next/link";
import React from "react";
import { FiArrowRight, FiCalendar, FiChevronDown } from "react-icons/fi";
import type { EstablishmentPublic, PublicProvider, PublicService } from "../types";
import type { PublicProduct } from "../../../services/public";
import type { CustomerMembershipPlan } from "../../../services/customerMemberships";
import { LocationInfo } from "./LocationInfo";
import { ProfileServicesPreview } from "./ProfileServicesPreview";
import { ProfileProvidersPreview } from "./ProfileProvidersPreview";
import { ProfileTimesPreview } from "./ProfileTimesPreview";
import { ProfileProductsPreview } from "./ProfileProductsPreview";
import { ProfileMembershipPreview } from "./ProfileMembershipPreview";
import { ProfileAccountInvite } from "./ProfileAccountInvite";
import { useScrollToOfferSection } from "../hooks/useOfferLanding";

interface ProfileBookingEntryProps {
  establishment: EstablishmentPublic;
  href: string;
  label: string;
  services: PublicService[];
  providers: PublicProvider[];
  times: string[];
  products: PublicProduct[];
  membershipPlans: CustomerMembershipPlan[];
  membershipHref: string;
  date: string;
  loadingTimes: boolean;
  isLoggedIn: boolean;
  isLoadingCustomer: boolean;
  customerFirstName?: string;
  customerPhotoUrl?: string | null;
  customerAreaHref: string;
  loginHref: string;
  signupHref: string;
  onLogout: () => void;
}

export function ProfileBookingEntry({
  establishment,
  href,
  label,
  services,
  providers,
  times,
  products,
  membershipPlans,
  membershipHref,
  date,
  loadingTimes,
  isLoggedIn,
  isLoadingCustomer,
  customerFirstName,
  customerPhotoUrl,
  customerAreaHref,
  loginHref,
  signupHref,
  onLogout,
}: ProfileBookingEntryProps) {
  const corpoRef = React.useRef<HTMLDivElement | null>(null);
  const [hasMoreBelow, setHasMoreBelow] = React.useState(false);

  // OFR-21: `?secao=assinaturas` ou `?secao=produtos` rola até a seção quando
  // ela chegar da API.
  useScrollToOfferSection();

  /**
   * A seta so existe enquanto ha conteudo abaixo da dobra. Sem isso ela
   * prometeria rolagem numa pagina que ja acabou.
   */
  React.useEffect(() => {
    const avaliar = () => {
      const restante =
        document.documentElement.scrollHeight - window.scrollY - window.innerHeight;
      setHasMoreBelow(restante > 80);
    };
    avaliar();

    window.addEventListener("scroll", avaliar, { passive: true });
    window.addEventListener("resize", avaliar);

    // Servicos, profissionais e horarios chegam por rede depois de montar: sem
    // observar o crescimento da pagina, a conta ficaria presa na altura inicial
    // e a seta nunca apareceria.
    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(avaliar);
      observer.observe(document.documentElement);
      if (document.body) observer.observe(document.body);
    }

    return () => {
      observer?.disconnect();
      window.removeEventListener("scroll", avaliar);
      window.removeEventListener("resize", avaliar);
    };
  }, []);

  /** Desliza ate a proxima secao ainda nao vista, nao um salto cego. */
  const onSeeMore = React.useCallback(() => {
    const corpo = corpoRef.current;
    if (!corpo) return;

    const barra = corpo.querySelector<HTMLElement>('[data-testid="profile-booking-bar"]');
    const limite = barra ? barra.getBoundingClientRect().top : window.innerHeight;

    const secoes = Array.from(corpo.children).filter(
      (filho) => filho.getAttribute("data-testid") !== "profile-booking-bar"
    );
    const proxima = secoes.find(
      (secao) => secao.getBoundingClientRect().top > limite - 24
    );

    if (!proxima) {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: "smooth",
      });
      return;
    }
    proxima.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <div className="space-y-4" data-testid="profile-booking-entry" ref={corpoRef}>
      <ProfileAccountInvite
        isLoggedIn={isLoggedIn}
        isLoadingCustomer={isLoadingCustomer}
        customerFirstName={customerFirstName}
        customerPhotoUrl={customerPhotoUrl}
        customerAreaHref={customerAreaHref}
        loginHref={loginHref}
        signupHref={signupHref}
        onLogout={onLogout}
      />

      <ProfileServicesPreview services={services} bookingHref={href} />

      <ProfileProvidersPreview providers={providers} bookingHref={href} />

      <ProfileTimesPreview
        date={date}
        times={times}
        loading={loadingTimes}
        bookingHref={href}
      />

      <ProfileProductsPreview products={products} bookingHref={href} />

      <ProfileMembershipPreview
        plans={membershipPlans}
        membershipHref={membershipHref}
        isLoggedIn={isLoggedIn}
      />

      <LocationInfo establishment={establishment} />

      {/* Barra fixa. Uma faixa opaca de ponta a ponta com borda em cima lia
          como fim de pagina — falso fundo: mais da metade do perfil ficava
          escondida e a pessoa agendava sem ver. Agora o conteudo passa
          visivelmente por baixo de um degrade, o botao e uma pastilha
          flutuante, e a seta ao lado desliza ate a proxima secao. */}
      <div
        aria-label="Agendar"
        role="region"
        data-testid="profile-booking-bar"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center bg-gradient-to-t from-[#f7f8fc] via-[#f7f8fc]/90 to-transparent px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-8"
      >
        <div className="pointer-events-auto flex w-full max-w-[520px] items-center gap-2">
          <Link
            href={href}
            data-testid="profile-booking-cta"
            className="inline-flex min-h-[56px] flex-1 items-center justify-center gap-2.5 rounded-2xl bg-gradient-to-r from-primary-600 to-primary-700 px-6 text-base font-bold leading-none text-white shadow-xl shadow-primary-900/25 transition hover:from-primary-hover hover:to-primary-600 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100"
          >
            <FiCalendar className="h-5 w-5" aria-hidden="true" />
            {label}
            <FiArrowRight className="h-5 w-5" aria-hidden="true" />
          </Link>

          {hasMoreBelow && (
            <button
              type="button"
              onClick={onSeeMore}
              aria-label="Ver mais informações do estabelecimento"
              data-testid="profile-see-more"
              className="inline-flex min-h-[56px] min-w-[56px] shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-600 shadow-lg shadow-slate-900/10 transition hover:border-primary-200 hover:text-primary-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100"
            >
              <FiChevronDown className="h-6 w-6" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
